<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="InertiaLink"
                :pages="[
                    {
                        name: $t('messages.inventory'),
                        href: meta.links.inventory_index,
                    },
                    {
                        name: $tc('messages.gamification_trades', 1),
                        href: meta.links.index,
                    },
                    {
                        name: $t('messages.edit'),
                        href: null,
                    },
                ]"
            />
        </template>
        <form action="" @submit.prevent="submit">
            <div class="md:grid md:grid-cols-2 md:gap-6">
                <div>
                    {{ $t('messages.you_give') }}:

                    <div
                        class="bg-gray-50 rounded rounded-lg p-4 flex flex-col justify-between"
                        style="min-height: 200px"
                    >
                        <svg
                            v-if="givenItems.length === 0"
                            class="mx-auto h-12 w-12 text-gray-400"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            aria-hidden="true"
                        >
                            <path
                                vector-effect="non-scaling-stroke"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z"
                            />
                        </svg>
                        <div v-else class="flex flex-wrap space-x-2">
                            <div
                                v-for="item in givenItems"
                                :key="`${item.id}_${item[trade.current]}`"
                                class="relative w-20 h-20"
                            >
                                <img :src="item.image_path" alt="" class="h-20 absolute" />
                                <BadgePill
                                    :color="item[trade.current] ? 'indigo' : 'gray'"
                                    class="absolute bottom-0 right-0"
                                >
                                    &times;{{ item[trade.current] }}
                                </BadgePill>
                            </div>
                        </div>

                        <div>
                            <div class="mt-1 relative rounded-md shadow-sm">
                                <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <img class="h-6 w-6" src="/assets/gamification/currency/coin.svg" alt="emerald" />
                                </div>
                                <input
                                    type="number"
                                    v-model.number="form.coins[trade.current]"
                                    step="1"
                                    min="0"
                                    class="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
                                    :placeholder="$t('messages.coins_amount')"
                                />
                            </div>
                            <FormError :value="form.errors[`coins.${trade.current}`]" class="mt-2" />
                        </div>
                    </div>
                </div>

                <div>
                    {{ $t('messages.you_receive') }}:

                    <div
                        class="bg-gray-50 rounded rounded-lg p-4 flex flex-col justify-between"
                        style="min-height: 200px"
                    >
                        <svg
                            v-if="requestedItems.length === 0"
                            class="mx-auto h-12 w-12 text-gray-400"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            aria-hidden="true"
                        >
                            <path
                                vector-effect="non-scaling-stroke"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z"
                            />
                        </svg>
                        <div v-else class="flex flex-wrap space-x-2">
                            <div
                                v-for="item in requestedItems"
                                :key="`${item.id}_${item[trade.other]}`"
                                class="relative w-20 h-20"
                            >
                                <img :src="item.image_path" alt="" class="h-20 absolute" />
                                <BadgePill
                                    :color="item[trade.other] ? 'indigo' : 'gray'"
                                    class="absolute bottom-0 right-0"
                                >
                                    &times;{{ item[trade.other] }}
                                </BadgePill>
                            </div>
                        </div>

                        <div>
                            <div class="mt-1 relative rounded-md shadow-sm">
                                <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <img class="h-6 w-6" src="/assets/gamification/currency/coin.svg" alt="emerald" />
                                </div>
                                <input
                                    type="number"
                                    v-model.number="form.coins[trade.other]"
                                    step="1"
                                    min="0"
                                    class="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
                                    :placeholder="$t('messages.coins_amount')"
                                />
                            </div>
                            <FormError :value="form.errors[`coins.${trade.other}`]" class="mt-2" />
                        </div>
                    </div>
                </div>
            </div>

            <div>
                <transition
                    enter-active-class="transition ease-out duration-100"
                    enter-from-class="transition ease-out duration-100"
                    enter-to-class="transform opacity-100 scale-100"
                    leave-active-class="transition ease-in duration-75"
                    leave-from-class="transform opacity-100 scale-100"
                    leave-to-class="transform opacity-0 scale-95"
                >
                    <ProgressBar :percent="percent" v-if="percent !== null" :show-text="false" color="gray" />
                </transition>
            </div>

            <div>{{ givenItemsPrice }} / {{ requestedItemsPrice }}</div>

            <div class="mt-3">
                <AppButton desired-action element="button" type="submit">
                    {{ $t('messages.continue') }}
                </AppButton>
            </div>

            <EmptyState picture v-if="items.length === 0">
                <template v-slot:picture />
            </EmptyState>
            <Tabs class="mt-5" v-else>
                <Tab :title="$tc('messages.gamification_items', 2)">
                    <div class="mt-5">
                        <div class="md:grid md:grid-cols-3 lg:grid-cols-6 md:gap-6">
                            <div v-for="item in items" :key="`${item.id}_${item.count}`">
                                <img :src="item.image_path" alt="" />
                                <div>
                                    {{ item.name }}
                                </div>
                                <div class="text-gray-600 text-sm">
                                    {{ item.options_description }}
                                </div>
                                <div class="text-gray-600 text-sm" v-if="item.description">
                                    <details class="mt-3 sm:col-span-12">
                                        <summary>
                                            <svg
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                                xmlns="http://www.w3.org/2000/svg"
                                                class="w-6 h-6 text-gray-400"
                                            >
                                                <path
                                                    stroke-linecap="round"
                                                    stroke-linejoin="round"
                                                    stroke-width="2"
                                                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                                ></path>
                                            </svg>
                                        </summary>
                                        {{ item.description }}
                                    </details>
                                </div>
                                <div class="flex justify-between items-center mt-1">
                                    <div>
                                        <BadgePill :color="item.count ? 'indigo' : 'gray'">
                                            &times;{{ item.count }}
                                        </BadgePill>
                                    </div>
                                </div>
                                <div
                                    class="flex justify-between items-center mt-1"
                                    v-if="item.required_level_description"
                                >
                                    {{ $t('messages.level_required') }}:
                                    {{ item.required_level_description }}
                                </div>

                                <div>
                                    <div class="flex justify-between py-2">
                                        <div>
                                            {{ $t('messages.request') }}
                                            <span v-if="item[trade.other]">({{ item[trade.other] }})</span>
                                        </div>

                                        <div class="flex justify-between space-x-2">
                                            <button
                                                type="button"
                                                @click="decreaseRequestItem(item)"
                                                :class="item[trade.other] > 0 ? 'text-indigo-500' : 'text-gray-300'"
                                            >
                                                <svg
                                                    class="w-6 h-6"
                                                    fill="currentColor"
                                                    viewBox="0 0 20 20"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                >
                                                    <path
                                                        fill-rule="evenodd"
                                                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM7 9a1 1 0 000 2h6a1 1 0 100-2H7z"
                                                        clip-rule="evenodd"
                                                    ></path>
                                                </svg>
                                            </button>

                                            <button type="button" @click="increaseRequestItem(item)">
                                                <svg
                                                    class="w-6 h-6 text-indigo-500"
                                                    fill="currentColor"
                                                    viewBox="0 0 20 20"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                >
                                                    <path
                                                        fill-rule="evenodd"
                                                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z"
                                                        clip-rule="evenodd"
                                                    ></path>
                                                </svg>
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <div class="flex justify-between py-2">
                                        <div>
                                            {{ $t('messages.give') }}
                                            <span
                                                v-if="item[trade.current]"
                                                :class="{
                                                    'text-red-600': item.count < item[trade.current],
                                                }"
                                                >({{ item[trade.current] }} / {{ item.count }})</span
                                            >
                                        </div>

                                        <div class="flex justify-between space-x-2">
                                            <button
                                                type="button"
                                                @click="decreaseGivenItem(item)"
                                                :class="item[trade.current] > 0 ? 'text-indigo-500' : 'text-gray-300'"
                                            >
                                                <svg
                                                    class="w-6 h-6"
                                                    fill="currentColor"
                                                    viewBox="0 0 20 20"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                >
                                                    <path
                                                        fill-rule="evenodd"
                                                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM7 9a1 1 0 000 2h6a1 1 0 100-2H7z"
                                                        clip-rule="evenodd"
                                                    ></path>
                                                </svg>
                                            </button>

                                            <button
                                                type="button"
                                                @click="increaseGivenItem(item)"
                                                :class="
                                                    item[trade.current] < item.count
                                                        ? 'text-indigo-500'
                                                        : 'text-gray-300'
                                                "
                                            >
                                                <svg
                                                    class="w-6 h-6"
                                                    fill="currentColor"
                                                    viewBox="0 0 20 20"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                >
                                                    <path
                                                        fill-rule="evenodd"
                                                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z"
                                                        clip-rule="evenodd"
                                                    ></path>
                                                </svg>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </Tab>
                <Tab :title="$tc('messages.card_collections', 2)" :selected="false">
                    <EmptyState picture v-if="cardCollections.length === 0">
                        <template v-slot:picture />
                    </EmptyState>

                    <div class="bg-gray-50 p-2 mt-3" v-else>
                        <div
                            class="col-span-1 bg-white rounded-lg shadow divide-y divide-gray-200"
                            v-for="cardCollection in cardCollections"
                            :key="cardCollection.id"
                        >
                            <div class="w-full flex items-center justify-between p-6 space-x-6">
                                <div class="flex-1 truncate">
                                    <div class="flex items-center space-x-3 justify-between">
                                        <h3 class="text-gray-900 text-sm font-medium truncate">
                                            {{ cardCollection.name }}
                                        </h3>
                                    </div>
                                    <div class="grid grid-cols-2 md:grid-cols-6 gap-2 md:gap-6 mt-1">
                                        <div
                                            v-for="card in cardCollection.cards"
                                            class=""
                                            :title="card.name"
                                            :key="card.id"
                                        >
                                            <div class="w-full relative aspect-w-1 aspect-h-1">
                                                <div
                                                    class="w-full h-full absolute bg-no-repeat bg-center bg-contain"
                                                    :class="{
                                                        'filter saturate-0': !card.count,
                                                    }"
                                                    :style="`background-image: url(${card.image_path});`"
                                                />
                                            </div>
                                            <div class="flex items-center justify-between mt-1">
                                                <CardRarityIndicator :rarity="card.rarity" />
                                                <BadgePill :color="card.count ? 'indigo' : 'gray'">
                                                    &times;{{ card.count }}
                                                </BadgePill>
                                            </div>
                                            <div class="flex items-center justify-between mt-1">
                                                {{ card.name }}
                                            </div>
                                            <div>
                                                <div class="flex justify-between py-2">
                                                    <div>
                                                        {{ $t('messages.request') }}
                                                        <span v-if="card[trade.other]">({{ card[trade.other] }})</span>
                                                    </div>

                                                    <div class="flex justify-between space-x-2">
                                                        <button
                                                            type="button"
                                                            @click="decreaseRequestCard(card)"
                                                            :class="
                                                                card[trade.other] > 0
                                                                    ? 'text-indigo-500'
                                                                    : 'text-gray-300'
                                                            "
                                                        >
                                                            <svg
                                                                class="w-6 h-6"
                                                                fill="currentColor"
                                                                viewBox="0 0 20 20"
                                                                xmlns="http://www.w3.org/2000/svg"
                                                            >
                                                                <path
                                                                    fill-rule="evenodd"
                                                                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM7 9a1 1 0 000 2h6a1 1 0 100-2H7z"
                                                                    clip-rule="evenodd"
                                                                ></path>
                                                            </svg>
                                                        </button>

                                                        <button type="button" @click="increaseRequestCard(card)">
                                                            <svg
                                                                class="w-6 h-6 text-indigo-500"
                                                                fill="currentColor"
                                                                viewBox="0 0 20 20"
                                                                xmlns="http://www.w3.org/2000/svg"
                                                            >
                                                                <path
                                                                    fill-rule="evenodd"
                                                                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z"
                                                                    clip-rule="evenodd"
                                                                ></path>
                                                            </svg>
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>

                                            <div v-if="card.count">
                                                <div class="flex justify-between py-2">
                                                    <div>
                                                        {{ $t('messages.give') }}
                                                        <span
                                                            v-if="card[trade.current]"
                                                            :class="{
                                                                'text-red-600': card.count < card[trade.current],
                                                            }"
                                                            >({{ card[trade.current] }}
                                                            /
                                                            {{ card.count }})</span
                                                        >
                                                    </div>

                                                    <div class="flex justify-between space-x-2">
                                                        <button
                                                            type="button"
                                                            @click="decreaseGivenCard(card)"
                                                            :class="
                                                                card[trade.current] > 0
                                                                    ? 'text-indigo-500'
                                                                    : 'text-gray-300'
                                                            "
                                                        >
                                                            <svg
                                                                class="w-6 h-6"
                                                                fill="currentColor"
                                                                viewBox="0 0 20 20"
                                                                xmlns="http://www.w3.org/2000/svg"
                                                            >
                                                                <path
                                                                    fill-rule="evenodd"
                                                                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM7 9a1 1 0 000 2h6a1 1 0 100-2H7z"
                                                                    clip-rule="evenodd"
                                                                ></path>
                                                            </svg>
                                                        </button>

                                                        <button
                                                            type="button"
                                                            @click="increaseGivenCard(card)"
                                                            :class="
                                                                card[trade.current] < card.count
                                                                    ? 'text-indigo-500'
                                                                    : 'text-gray-300'
                                                            "
                                                        >
                                                            <svg
                                                                class="w-6 h-6"
                                                                fill="currentColor"
                                                                viewBox="0 0 20 20"
                                                                xmlns="http://www.w3.org/2000/svg"
                                                            >
                                                                <path
                                                                    fill-rule="evenodd"
                                                                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z"
                                                                    clip-rule="evenodd"
                                                                ></path>
                                                            </svg>
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </Tab>
            </Tabs>
        </form>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import AppButton from '@/components/AppButton.vue';
import EmptyState from '@/components/EmptyState.vue';
import BadgePill from '@/components/BadgePill.vue';
import Tabs from '@/components/Tabs.vue';
import Tab from '@/components/Tab.vue';
import CardRarityIndicator from '@/components/CardRarityIndicator.vue';
import FormError from '@/components/Forms/FormError.vue';
import ProgressBar from '@/components/Gamification/ProgressBar.vue';

export default {
    components: {
        ProgressBar,
        FormError,
        EmptyState,
        PanelPage,
        AppButton,
        BadgePill,
        Tabs,
        Tab,
        CardRarityIndicator,
    },
    layout: Layout,
    name: 'Edit',
    props: ['meta', 'items', 'filters', 'cardCollections', 'trade'],
    data() {
        return {
            form: this.$inertia.form({
                coins: {
                    first: this.trade.coins.first,
                    second: this.trade.coins.second,
                },
            }),
        };
    },
    methods: {
        submit() {
            this.form
                .transform((data) => {
                    data.items = this.items
                        .filter((i) => i.first + i.second > 0)
                        .map((i) => {
                            return {
                                id: i.id,
                                first: i.first,
                                second: i.second,
                            };
                        });

                    data.cards = this.cardCollections
                        .flatMap((c) => c.cards)
                        .filter((i) => i.first + i.second > 0)
                        .map((i) => {
                            return {
                                id: i.id,
                                first: i.first,
                                second: i.second,
                            };
                        });

                    return data;
                })
                .patch(this.meta.links.update);
        },
        increaseRequestItem(item) {
            if (item[this.trade.other] < 15) {
                item[this.trade.other]++;
            }
        },
        decreaseRequestItem(item) {
            if (item[this.trade.other] > 0) {
                item[this.trade.other]--;
            }
        },
        increaseGivenItem(item) {
            if (item[this.trade.current] < item.count) {
                item[this.trade.current]++;
            }
        },
        decreaseGivenItem(item) {
            if (item[this.trade.current] > 0) {
                item[this.trade.current]--;
            }
        },
        increaseRequestCard(card) {
            if (card[this.trade.other] < 15) {
                card[this.trade.other]++;
            }
        },
        decreaseRequestCard(card) {
            if (card[this.trade.other] > 0) {
                card[this.trade.other]--;
            }
        },
        increaseGivenCard(card) {
            if (card[this.trade.current] < card.count) {
                card[this.trade.current]++;
            }
        },
        decreaseGivenCard(card) {
            if (card[this.trade.current] > 0) {
                card[this.trade.current]--;
            }
        },
    },
    computed: {
        givenItems: function () {
            let cards = this.cardCollections.flatMap((c) => c.cards).filter((i) => i[this.trade.current]);
            let items = this.items.filter((i) => i[this.trade.current]);
            return [...items, ...cards];
        },
        requestedItems: function () {
            let cards = this.cardCollections.flatMap((c) => c.cards).filter((i) => i[this.trade.other]);
            let items = this.items.filter((i) => i[this.trade.other]);
            return [...items, ...cards];
        },
        givenItemsPrice: function () {
            return (
                this.givenItems.map((i) => i.price * i[this.trade.current]).reduce((a, b) => a + b, 0) +
                this.form.coins[this.trade.current]
            );
        },
        requestedItemsPrice: function () {
            return (
                this.requestedItems.map((i) => i.price * i[this.trade.other]).reduce((a, b) => a + b, 0) +
                this.form.coins[this.trade.other]
            );
        },
        percent: function () {
            const total = this.givenItemsPrice + this.requestedItemsPrice;

            if (total === 0) {
                return null;
            }

            return Math.round((this.givenItemsPrice / total) * 100);
        },
    },
};
</script>

<style scoped>
details summary::-webkit-details-marker,
details summary::marker {
    display: none;
    content: '';
}
</style>
